import { useEffect } from "react";
import { API_LOANS } from "../../constants/apis";
import { COMPONENT, LOANS } from "../../constants/constants";
import { TSet } from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { reloadData } from "../../components/lists/ListHandler";
import { InfoBox } from "../../components/elements/Elements";
import { currencyFormat, formatDate, stringify } from "../../utils/helpers";
import useGet from "../common/useGet";
import { SimpleLoader } from "../../components/common/Loader";
import { CalculatorCard } from "../../components/elements/CardElements";
import { INFO_YELLOW, PRIMARY_COLOR } from "../../constants/colors";
import { alpha } from "@mui/material";

export const useLoanOfferAcceptForm = ({ state }: any) => {
  const loan = state || {};
  const { id: loanId, purpose, amount } = loan;

  // Fetch the offer details
  const { loadData, data: offer, loading } = useGet();

  useEffect(() => {
    if (loanId) {
      loadData({ api: `${API_LOANS}${loanId}/offer/` });
    }
  }, [loanId]);

  const fields = [
    // Loading state
    {
      type: COMPONENT,
      visible: loading,
      component: <SimpleLoader />,
    },
    // Header
    {
      type: COMPONENT,
      visible: !loading && !!offer,
      component: (
        <div className="textCenter flexColumn gap5 p10">
          <div className="font16 bold textDark">Loan Offer</div>
          <div className="font13 grey capitalize">{stringify(purpose)}</div>
        </div>
      ),
    },
    // Offer terms
    {
      type: COMPONENT,
      visible: !loading && !!offer,
      component: offer ? (
        <CalculatorCard
          title="Offer Terms"
          fields={{
            principal: currencyFormat(offer.principal),
            interest_rate: `${offer.interest_rate}%`,
            interest_amount: currencyFormat(offer.interest_amount),
            total_repayment: currencyFormat(offer.total_repayment),
            platform_fee: currencyFormat(offer.platform_fee),
            duration: `${offer.loan_duration_days} days`,
            repayment_frequency: stringify(offer.repayment_frequency),
            installments: offer.num_installments,
            installment_amount: currencyFormat(offer.installment_amount),
          }}
        />
      ) : null,
    },
    // Penalty terms
    {
      type: COMPONENT,
      visible: !loading && !!offer?.penalty_terms,
      component: offer?.penalty_terms ? (
        <div
          className="font12 p15 r10"
          style={{
            background: alpha(PRIMARY_COLOR, 0.04),
            border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
            lineHeight: 1.5,
          }}
        >
          <div className="semiBold textDark mb5">Late Payment Penalty</div>
          <div className="grey">{offer.penalty_terms}</div>
        </div>
      ) : null,
    },
    // Agreement
    {
      type: COMPONENT,
      visible: !loading && !!offer,
      component: (
        <InfoBox color={INFO_YELLOW}>
          By accepting this offer, you agree to the repayment terms above.
          Failure to make timely payments may result in penalties and affect
          your credit score.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = (): TSet => {
    return {
      api: `${API_LOANS}${loanId}/accept-offer/`,
      params: {},
      method: "POST",
      successMessage: { message: "Offer accepted! Your loan is being processed.", duration: 7 },
      onSuccess: () => {
        reloadData(LOANS);
        showConfetti();
      },
    };
  };

  return {
    fields,
    onSubmit,
    title: "Review Loan Offer",
    submitText: "Accept Offer",
    allowSubmit: !loading && !!offer,
  };
};